// ─── Store origin ────────────────────────────────────────────────────────────
export const STORE_ADDRESS = 'Lot 2555 Providence, East Bank Demerara, Guyana';
export const STORE_LABEL   = 'SipDash – Providence, EBD';

// ─── Delivery Zones (from Providence, EBD) ───────────────────────────────────
export interface DeliveryZone {
  name: string;
  label: string;       // human-friendly area name shown in UI
  etaMin: number;      // minutes
  etaMax: number;
  color: string;       // tailwind color class for badge
  keywords: string[];
}

export const DELIVERY_ZONES: DeliveryZone[] = [
  {
    name: 'ebd-near',
    label: 'East Bank – Near',
    etaMin: 10,
    etaMax: 20,
    color: 'green',
    keywords: [
      'providence', 'diamond', 'eccles', 'coverden', 'grove', 'soesdyke',
      "peter's hall", 'peters hall', 'east bank', 'ebd', 'herstelling',
      'houston', 'ruimveldt',
    ],
  },
  {
    name: 'georgetown',
    label: 'Georgetown',
    etaMin: 20,
    etaMax: 35,
    color: 'orange',
    keywords: [
      'georgetown', 'camp street', 'robb street', 'regent street', 'sheriff street',
      'bourda', 'lacytown', 'newtown', 'kitty', 'alberttown', 'queenstown',
      'werk-en-rust', 'werken-rust', 'lodge', 'south ruimveldt', 'north ruimveldt',
      'sophia', 'turkeyen', 'prashad nagar', 'cummingsburg', 'robbstown',
      'stabroek', 'water street', 'main street', 'middle street', 'young street',
    ],
  },
  {
    name: 'east-coast',
    label: 'East Coast Demerara',
    etaMin: 35,
    etaMax: 50,
    color: 'yellow',
    keywords: [
      'east coast', 'ecd', 'enmore', 'mon repos', 'lusignan', 'buxton',
      'non pareil', 'beterverwagting', 'enterprise', 'success', 'montrose',
      'foulis', 'golden grove', 'vigilance', 'hope', 'belfield',
    ],
  },
  {
    name: 'west-bank',
    label: 'West Bank Demerara',
    etaMin: 30,
    etaMax: 45,
    color: 'yellow',
    keywords: [
      'west bank', 'wbd', 'vreed en hoop', 'leonora', 'uitvlugt',
      'la grange', 'felicity', 'hague', 'cornelia ida',
    ],
  },
];

/** Default zone when address doesn't match any known area. */
const DEFAULT_ZONE: DeliveryZone = {
  name: 'default',
  label: 'Georgetown & surrounds',
  etaMin: 20,
  etaMax: 35,
  color: 'orange',
  keywords: [],
};

// ─── Zone detection ───────────────────────────────────────────────────────────

/**
 * Detects which delivery zone a given address belongs to.
 * Tries exact keyword matching first; falls back to DEFAULT_ZONE.
 */
export function detectZone(address: string): DeliveryZone {
  const lower = address.toLowerCase();
  for (const zone of DELIVERY_ZONES) {
    if (zone.keywords.some((kw) => lower.includes(kw))) {
      return zone;
    }
  }
  return DEFAULT_ZONE;
}

/**
 * Returns ETA in minutes.
 * Uses zone min + a small deterministic offset (based on string hash)
 * to give each address a slightly unique but stable value.
 */
export function estimateDeliveryMinutes(address: string): number {
  const zone = detectZone(address);
  const range = zone.etaMax - zone.etaMin;

  // deterministic jitter within the zone's range (0 to range)
  let hash = 0;
  for (let i = 0; i < address.length; i++) {
    hash = (hash << 5) - hash + address.charCodeAt(i);
    hash |= 0;
  }
  const jitter = Math.abs(hash) % (range + 1);
  return zone.etaMin + jitter;
}

/**
 * Returns a human-friendly ETA string like "22–28 min".
 */
export function formatEtaRange(address: string): string {
  const zone = detectZone(address);
  return `${zone.etaMin}–${zone.etaMax} min`;
}

/**
 * Returns the zone label + ETA badge info for a given address.
 */
export function getZoneInfo(address: string): {
  zone: DeliveryZone;
  eta: number;
  etaRange: string;
} {
  const zone = detectZone(address);
  return {
    zone,
    eta: estimateDeliveryMinutes(address),
    etaRange: `${zone.etaMin}–${zone.etaMax} min`,
  };
}

// ─── Google Maps helpers ──────────────────────────────────────────────────────

/**
 * Normalises destination: appends ", Guyana" if not already present.
 */
function normalise(destination: string): string {
  return /Georgetown|Demerara|Guyana/i.test(destination)
    ? destination
    : `${destination}, Georgetown, Guyana`;
}

/**
 * Builds a Google Maps embed URL showing driving directions
 * from the store to the customer's address.
 */
export function buildDirectionsEmbedUrl(destination: string): string {
  return (
    'https://maps.google.com/maps?saddr=' +
    encodeURIComponent(STORE_ADDRESS) +
    '&daddr=' +
    encodeURIComponent(normalise(destination)) +
    '&output=embed'
  );
}

/**
 * Opens the route in a new Google Maps tab.
 */
export function buildDirectionsUrl(destination: string): string {
  return (
    'https://www.google.com/maps/dir/' +
    encodeURIComponent(STORE_ADDRESS) +
    '/' +
    encodeURIComponent(normalise(destination))
  );
}
